//@flow
import React from "react"
import { Field, reduxForm } from "redux-form"
import {
  InputField,
  SelectField,
  CheckboxField,
  Label,
  FormGroup,
  Error,
  FormRow,
  FormCol,
  Note
} from "../UI/Form"
import Loader from "../UI/Loader"
import { H5 } from "../UI/Headings"
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  SecondaryClipboardButton
} from "../UI/Buttons"
import validate from "./validate"
import type { KioskFormProps } from "./types"

const accessUrl = (id: string): string => {
  const protocol = window ? window.location.protocol : "https:"
  const hostname = window ? window.location.hostname : ""
  const port =
    window && window.location.port !== "" ? `:${window.location.port}` : ""
  return `${protocol}//${hostname}${port}/go/${id}`
}

const KioskForm = ({
  handleSubmit,
  handleCancel,
  handleChange,
  handleDelete,
  handleInvite,
  inviteLoading,
  sensors,
  attractLoops,
  error,
  isNew,
  pristine,
  lastInviteSent,
  gasPreviewUrl,
  particlesPreviewUrl,
  id
}: KioskFormProps) => (
  <form onSubmit={handleSubmit}>
    <Note>Note: All fields REQUIRED</Note>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Label htmlFor="friendlyName">Friendly Name</Label>
          <Field
            name="friendlyName"
            component={InputField}
            type="text"
            onChange={handleChange}
          />
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note>A Name that will help you easily identify this kiosk</Note>
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Label htmlFor="shortName">Short Name</Label>
          <Field
            name="shortName"
            component={InputField}
            type="text"
            onChange={handleChange}
          />
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note>
          A shorter name that will be displayed in the UI copy referencing the
          location.
        </Note>
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Label htmlFor="email">Primary User Email</Label>
          <Field
            name="email"
            component={InputField}
            type="email"
            onChange={handleChange}
          />
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note />
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormRow>
          <FormCol grow={1}>
            <FormGroup>
              <Label htmlFor="latitude">Latitude</Label>
              <Field
                name="latitude"
                component={InputField}
                type="number"
                step="0.0000001"
                onChange={handleChange}
              />
            </FormGroup>
          </FormCol>
          <FormCol grow={1}>
            <FormGroup>
              <Label htmlFor="longitude">Longitude</Label>
              <Field
                name="longitude"
                component={InputField}
                type="number"
                step="0.0000001"
                onChange={handleChange}
              />
            </FormGroup>
          </FormCol>
        </FormRow>
      </FormCol>
      <FormCol grow={1}>
        <Note />
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Label htmlFor="primarySensor.id">Primary Sensor</Label>
          <Field
            name="primarySensor.id"
            component={SelectField}
            onChange={handleChange}
          >
            <option />
            {sensors &&
              sensors.map(({ id, friendlyName, stationName }) => (
                <option value={id} key={id}>
                  {friendlyName || stationName}
                </option>
              ))}
          </Field>
          {particlesPreviewUrl && gasPreviewUrl ? (
            <FormRow>
              <FormCol grow={2}>
                <p>
                  Check live data: <a href={particlesPreviewUrl}>particles</a> |{" "}
                  <a href={gasPreviewUrl}>gas</a>
                </p>
              </FormCol>
            </FormRow>
          ) : (
            <span />
          )}
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note />
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Label htmlFor="language">Language</Label>
          <Field
            name="language"
            component={SelectField}
            onChange={handleChange}
          >
            <option value="English">English</option>
            <option value="Spanish">Spanish</option>
          </Field>
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note />
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormRow>
          <FormCol grow={1}>
            <FormGroup>
              <Label htmlFor="startsAt">Starts At</Label>
              <Field
                name="startsAt"
                component={InputField}
                type="date"
                onChange={handleChange}
              />
            </FormGroup>
          </FormCol>
          <FormCol grow={1}>
            <FormGroup>
              <Label htmlFor="endsAt">Ends At</Label>
              <Field
                name="endsAt"
                component={InputField}
                type="date"
                onChange={handleChange}
              />
            </FormGroup>
          </FormCol>
        </FormRow>
      </FormCol>
      <FormCol grow={1}>
        <Note>
          Select a date far into the future to prevent kiosks from expiring too
          early.
        </Note>
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Field
            name="hidden"
            labelText="Hidden from fixed kiosk installations"
            component={CheckboxField}
            onChange={handleChange}
          />
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note>
          When checked, this kiosk's sensor data won't be visible to the
          semi-permanant kiosk installations.
        </Note>
      </FormCol>
    </FormRow>

    <FormRow>
      <FormCol grow={2}>
        <FormGroup>
          <Field
            name="usesVirtualKeyboard"
            labelText="Toggle the in app keyboard."
            component={CheckboxField}
            onChange={handleChange}
          />
        </FormGroup>
      </FormCol>
      <FormCol grow={1}>
        <Note>
          When checked on, the in app key board will appear on form fields.
        </Note>
      </FormCol>
    </FormRow>

    {error && <Error>{error}</Error>}

    <FormRow>
      <FormCol grow={2}>
        <FormRow>
          <FormCol grow={1} />
          <FormCol grow={1}>
            <SecondaryButton block={true} onClick={handleCancel} type="button">
              Cancel
            </SecondaryButton>
          </FormCol>
          <FormCol grow={1}>
            <PrimaryButton
              block={true}
              disabled={!isNew && pristine}
              type="submit"
            >
              {isNew ? "Create" : "Update"} Kiosk
            </PrimaryButton>
          </FormCol>
        </FormRow>
      </FormCol>
      <FormCol grow={1}>
        <Note>
          When you create a new kiosk the primary user will receive an email.
        </Note>
      </FormCol>
    </FormRow>
    {isNew ? (
      ""
    ) : (
      <div>
        <FormRow style={{ display: "none" }}>
          <FormCol grow={2}>
            <FormRow>
              <FormCol grow={1} />
              <FormCol grow={1} />
              <FormCol grow={1}>
                <DangerButton block={true} onClick={handleDelete} type="button">
                  Delete Kiosk
                </DangerButton>
              </FormCol>
            </FormRow>
          </FormCol>
          <FormCol grow={1}>
            <Note>This cannot be undone.</Note>
          </FormCol>
        </FormRow>
        <FormRow>
          <FormCol grow={2}>
            <FormRow>
              <FormCol grow={1}>
                <H5>Access URL</H5>
                <a href={accessUrl(id)}>{accessUrl(id)}</a>
              </FormCol>
            </FormRow>
            <FormRow>
              <FormCol grow={1}>
                <SecondaryClipboardButton data-clipboard-text={accessUrl(id)}>
                  Copy to Clipboard
                </SecondaryClipboardButton>
              </FormCol>
              <FormCol grow={1}>
                <Loader loading={inviteLoading}>
                  <SecondaryButton
                    type="button"
                    onClick={e => {
                      e.preventDefault()
                      handleInvite()
                    }}
                    disabled={lastInviteSent !== ""}
                  >
                    {lastInviteSent === ""
                      ? "Resend Invitation"
                      : `Sent at ${lastInviteSent}`}
                  </SecondaryButton>
                </Loader>
              </FormCol>
            </FormRow>
          </FormCol>
          <FormCol grow={1}>
            <Note>
              The access url provides a temporary access point for this kiosk
              user to automatically and privately connect to the Kiosk
              interface.
            </Note>
          </FormCol>
        </FormRow>
      </div>
    )}
  </form>
)

export default reduxForm({
  form: "kiosk",
  enableReinitialize: true,
  validate
})(KioskForm)



// WEBPACK FOOTER //
// ./src/Kiosks/KioskForm.js